import { useCallback, useRef, useState } from "react";
import useApi from "./useApi";
import { createPostIfDraft, getPostIf, getPostIfAttributeCatalogue, getPostIfCanAct } from "../services/pifService";
import { PifDraft } from "../pages/supervision/PostInterviewForm/types";
import {
  CreatePostIfDraftRequestDto,
  PostIfAttributeValueDto,
  PostIfCanActResultDto,
  PostIfDto,
  PostIfResultDto,
} from "../types/pifDto";

/** Builds the fieldKey → postIfAttributeId map save-answers writes against. */
export const toAttributeIdMap = (answers: PostIfAttributeValueDto[]): Record<string, number> =>
  (answers || []).reduce<Record<string, number>>((map, answer) => {
    if (answer?.fieldKey) map[answer.fieldKey] = answer.postIfAttributeId;
    return map;
  }, {});

/**
 * Owns the PostIF draft lifecycle.
 *
 * Nothing is stored until the supervisor SAVES. `open` only reads the field catalogue, which is
 * what supplies each field's `postIfAttributeId`; the Portal_PostIF row is written later, by
 * `ensureCreated`, on the first save-applicant/save-answers.
 *
 * It used to be the other way round: `open` called create-draft the moment an Employee ID
 * resolved, because the catalogue was reachable only through GET /postif/{id}. That left an empty
 * row behind whenever the supervisor walked away, and the prefill from the lookup - which is only
 * ever in the browser until a save - was lost on reopen, leaving a blank form nobody could even
 * identify (Bug 79824). GET /postif/attributes returns the same catalogue without a record.
 *
 * Errors surface through useApi's central alert; `open` resolves to null so the caller can keep
 * the supervisor on the start screen rather than opening a form that cannot save its answers.
 */
const usePifDraft = () => {
  const { executeApi } = useApi();
  const [draft, setDraft] = useState<PifDraft | null>(null);
  const [catalogue, setCatalogue] = useState<PostIfAttributeValueDto[]>([]);
  const [opening, setOpening] = useState(false);
  // Held from `open` until the first save, which is when the row is finally written.
  const pendingCreate = useRef<CreatePostIfDraftRequestDto | null>(null);

  const open = useCallback(
    async (request: CreatePostIfDraftRequestDto): Promise<PifDraft | null> => {
      setOpening(true);
      try {
        // Without the catalogue no EAV answer can be addressed at all - every save-answers call
        // would resolve to an empty attributeIdByFieldKey and silently drop every field. Fail the
        // whole open() rather than hand back a half-usable form; useApi has already alerted.
        const answers = await executeApi(getPostIfAttributeCatalogue, undefined);
        if (!answers) return null;

        pendingCreate.current = request;
        const next: PifDraft = {
          postIfId: null,
          refNo: null,
          attributeIdByFieldKey: toAttributeIdMap(answers),
        };
        setDraft(next);
        setCatalogue(answers);
        return next;
      } finally {
        setOpening(false);
      }
    },
    [executeApi],
  );

  /**
   * The id to save against, creating the row on the way if this is the first save. Returns null
   * when the create fails, so the caller abandons the save rather than writing against no record.
   *
   * Idempotent: once a row exists - whether created here or loaded by `load` - it is reused.
   */
  const ensureCreated = useCallback(async (): Promise<number | null> => {
    if (draft?.postIfId) return draft.postIfId;

    const request = pendingCreate.current;
    if (!request) return null;

    const created = await executeApi<CreatePostIfDraftRequestDto, PostIfResultDto>(createPostIfDraft, request);
    const postIfId = created?.postIfId;
    if (!postIfId) return null;

    pendingCreate.current = null;
    setDraft((prev) =>
      prev ? { ...prev, postIfId, refNo: created.refNo ?? prev.refNo } : prev,
    );
    return postIfId;
  }, [draft?.postIfId, executeApi]);

  /**
   * Re-opens an existing PostIF by id, for `/hiring/postif/:id`.
   *
   * Checks `can-act` first so a supervisor who may not touch this form gets a clean refusal
   * rather than a half-rendered page — the API never distinguishes "missing" from
   * "forbidden", so neither do we. Returns the whole DTO so the caller can hydrate the form
   * from the header, the applicant row and the saved answers.
   */
  const load = useCallback(
    async (postIfId: number): Promise<PostIfDto | null> => {
      setOpening(true);
      try {
        const permission = await executeApi<{ postIfId: number; action: string }, PostIfCanActResultDto>(
          getPostIfCanAct,
          { postIfId, action: "edit" },
        );
        if (!permission?.allowed) return null;

        const detail = await executeApi<number, PostIfDto>(getPostIf, postIfId);
        if (!detail?.header) return null;

        const answers = detail.answers ?? [];
        setDraft({
          postIfId: detail.header.postIfId || postIfId,
          refNo: detail.header.refNo ?? null,
          attributeIdByFieldKey: toAttributeIdMap(answers),
        });
        setCatalogue(answers);
        return detail;
      } finally {
        setOpening(false);
      }
    },
    [executeApi],
  );

  const reset = useCallback(() => {
    setDraft(null);
    setCatalogue([]);
    // "Start another" must not inherit the previous form's pending create request.
    pendingCreate.current = null;
  }, []);

  return { draft, catalogue, opening, open, ensureCreated, load, reset };
};

export default usePifDraft;
